import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { timezoneUtils } from "@/lib/timezone";
import type { Holiday, HolidayFilters, HolidayFormData, HolidayTarget } from "../types";
import { useCreateHoliday, useDeleteHoliday, useUpdateHoliday } from "./use-holiday-mutation";
import { useHolidayQuery } from "./use-holiday-query";

export function useHolidays(filters?: HolidayFilters) {
  const { data, loading, error, refetch } = useHolidayQuery(filters);
  const { execute: createHoliday, isLoading: creating } = useCreateHoliday();
  const { execute: updateHoliday, isLoading: updating } = useUpdateHoliday();
  const { execute: deleteHoliday, isLoading: deleting } = useDeleteHoliday();

  // Transform holidays to include salon timezone DateTime objects
  const holidays = useMemo(() => {
    if (!data?.getHolidays) return [];

    return data.getHolidays.map((holiday: Holiday) => ({
      ...holiday,
      startDateTimeLocal: timezoneUtils.fromISO(holiday.startDateTime),
      endDateTimeLocal: timezoneUtils.fromISO(holiday.endDateTime),
    }));
  }, [data]);

  // Filter holidays by time
  const futureHolidays = useMemo(() => {
    const now = DateTime.now();
    return holidays.filter((holiday) => timezoneUtils.fromISO(holiday.endDateTime) >= now);
  }, [holidays]);

  const pastHolidays = useMemo(() => {
    const now = DateTime.now();
    return holidays.filter((holiday) => timezoneUtils.fromISO(holiday.endDateTime) < now);
  }, [holidays]);

  const createSalonHoliday = useCallback(
    async (input: HolidayFormData, target?: HolidayTarget) => {
      return createHoliday(input, target);
    },
    [createHoliday]
  );

  const updateSalonHoliday = useCallback(
    async (id: string, input: Partial<HolidayFormData>) => {
      return updateHoliday({ id, ...input });
    },
    [updateHoliday]
  );

  const deleteSalonHoliday = useCallback(
    async (holidayId: string) => {
      return deleteHoliday(holidayId);
    },
    [deleteHoliday]
  );

  return {
    // Data
    holidays,
    futureHolidays,
    pastHolidays,

    // State
    loading,
    error,
    creating,
    updating,
    deleting,

    // Actions
    createHoliday: createSalonHoliday,
    updateHoliday: updateSalonHoliday,
    deleteHoliday: deleteSalonHoliday,
    refetch,
  };
}
